/**
 * @fileoverview Checkout module for checkout flow integration.
 */

import { postToHost, onMessage } from "../../core/messenger";
import {
  sendRequest,
  handleResponse,
  generateRequestId,
} from "../../core/requests";
import { createSubscription } from "../../core/subscriptions";
import type { BaseMessage, CheckoutResponseMessage } from "../../core/types";
import {
  validateCheckout,
  logValidationErrors,
  CHECKOUT_ID_MIXED_MESSAGE,
} from "../../core/validation";
import type { CheckoutValidationInput } from "../../core/validation/types";
import { CHECKOUT_EVENTS } from "./events";
import type {
  AddonInfo,
  CheckoutModule,
  CheckoutCreateConfig,
  CheckoutResultCallback,
  CheckoutItem,
  CheckoutByIdInput,
  CheckoutInputError,
  GetAddonsResult,
} from "./types";

export type {
  AddonInfo,
  CheckoutModule,
  CheckoutCreateConfig,
  CheckoutResult,
  CheckoutResultCallback,
  CheckoutItem,
  CheckoutByIdInput,
  CheckoutInputError,
  GetAddonsResult,
} from "./types";

/** Error code thrown for malformed create() input */
export const INVALID_CHECKOUT_INPUT = "INVALID_CHECKOUT_INPUT";

function invalidInput(message: string): CheckoutInputError {
  const error = new Error(message) as CheckoutInputError;
  error.code = INVALID_CHECKOUT_INPUT;
  return error;
}

/**
 * Fields of the single-item (`CheckoutItem`) input form. A `checkout_id` input
 * must not carry any of these — that mixing is rejected before posting.
 */
const ITEM_FIELDS = ["type", "slug", "quantity"];

function isCheckoutById(input: unknown): input is CheckoutByIdInput {
  return (
    typeof input === "object" &&
    input !== null &&
    !Array.isArray(input) &&
    "checkout_id" in input &&
    (input as { checkout_id?: unknown }).checkout_id !== undefined
  );
}

/** Response message for getAddons */
interface GetAddonsResponseMessage extends BaseMessage {
  payload: {
    success: boolean;
    addons?: AddonInfo[];
    error?: { code: string; message: string };
  };
}

/**
 * Create the checkout module.
 */
export function createCheckoutModule(): CheckoutModule {
  const resultSubscription = createSubscription<CheckoutResultCallback>();

  // Track listener unsubscribe functions for cleanup
  const unsubscribers: Array<() => void> = [];

  // Listen for checkout responses
  unsubscribers.push(
    onMessage<CheckoutResponseMessage>(CHECKOUT_EVENTS.RESPONSE, (message) => {
      resultSubscription.notify({
        success: message.payload.success,
        order_id: message.payload.order_id,
        checkout_id: message.payload.checkout_id,
        status: message.payload.status,
        error: message.payload.error,
        context: message.payload.context,
      });
    }),
  );

  // Listen for getAddons responses
  unsubscribers.push(
    onMessage<GetAddonsResponseMessage>(
      CHECKOUT_EVENTS.GET_ADDONS_RESPONSE,
      (message) => {
        if (message.requestId) {
          handleResponse<GetAddonsResult>(message.requestId, {
            success: message.payload.success,
            addons: message.payload.addons,
            error: message.payload.error,
          });
        }
      },
    ),
  );

  return {
    create(
      input: CheckoutItem | CheckoutItem[] | CheckoutByIdInput,
      config?: CheckoutCreateConfig,
    ): void {
      if (Array.isArray(input) && input.some((i) => isCheckoutById(i))) {
        throw invalidInput(CHECKOUT_ID_MIXED_MESSAGE);
      }

      let payload: Record<string, unknown>;

      if (isCheckoutById(input)) {
        // `context` belongs to config.context. Don't extend this check to the
        // item form: it has always dropped the key, and throwing would break
        // existing callers.
        if ("context" in input) {
          throw invalidInput(
            "context must be passed as the second argument (config.context)",
          );
        }
        // Flat CheckoutItem fields (type/slug/quantity) belong to the item
        // form only; reject them alongside checkout_id.
        if (ITEM_FIELDS.some((field) => field in input)) {
          throw invalidInput(CHECKOUT_ID_MIXED_MESSAGE);
        }
        // The validator is the single owner of the checkout_id + item/items
        // mixing rule — pass the full input through.
        const validation = validateCheckout(
          input as unknown as CheckoutValidationInput,
        );
        if (!validation.valid) {
          logValidationErrors(CHECKOUT_EVENTS.CREATE, validation.errors);
          throw invalidInput(validation.errors[0]);
        }
        payload = { checkout_id: input.checkout_id };
      } else {
        const items = Array.isArray(input) ? input : [input as CheckoutItem];

        const validation = validateCheckout({ items });
        if (!validation.valid) {
          logValidationErrors(CHECKOUT_EVENTS.CREATE, validation.errors);
          throw invalidInput(validation.errors[0]);
        }

        payload = {
          items: items.map((i) => ({
            type: i.type,
            slug: i.slug,
            quantity: i.quantity ?? 1,
          })),
        };
      }

      // Note: Using "*" as targetOrigin because the SDK runs in third-party
      // iframes and doesn't know the host's origin at build time. Origin
      // validation is performed by the host via its trusted origin list.
      postToHost(
        CHECKOUT_EVENTS.CREATE,
        {
          ...payload,
          ...(config?.context !== undefined && { context: config.context }),
        },
        "*",
        generateRequestId(),
      );
    },

    onResult(callback: CheckoutResultCallback): () => void {
      return resultSubscription.subscribe(callback);
    },

    async getAddons(): Promise<GetAddonsResult> {
      try {
        return await sendRequest<GetAddonsResult>(
          CHECKOUT_EVENTS.GET_ADDONS,
          {},
          30000,
        );
      } catch (error) {
        return {
          success: false,
          error: {
            code: "REQUEST_FAILED",
            message: error instanceof Error ? error.message : "Unknown error",
          },
        };
      }
    },

    resetCache(): void {
      postToHost(CHECKOUT_EVENTS.RESET_CACHE, {}, "*");
    },

    destroy(): void {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      unsubscribers.length = 0;
      resultSubscription.clear();
    },
  };
}
